export type EntityToolTabCounts = {
  'All Matches': number;
  Ownership?: number;
};

export type EntityToolTableChrome = {
  tabCounts: EntityToolTabCounts;
  totalForPagination: number;
  /**
   * The rows on screen are one server page and an in-browser Table Filter /
   * search is narrowing them, so the visible match count describes this page
   * only — the footer says so instead of claiming it as the result total.
   */
  isPageScopedFiltering: boolean;
};

/**
 * Tab badges, footer "of N", and pager page count for the Entity Screening
 * results table.
 *
 * `clientOnlyFilterCount` counts the filters the browser applied by itself —
 * anything the parent pushed into the screening query is already reflected in
 * the backend Hit Total and must not be counted here. Those browser-side
 * filters run over the rows the table is currently holding, so chrome may
 * only follow their filtered counts when the browser holds the whole result
 * set (client pagination). When rows arrive one server page at a time, such a
 * filter is just a view on that page: badges and the pager keep the backend
 * Hit Total — collapsing the pager to the matches on the loaded page would
 * strand every later page — and the footer reports the page-scoped match
 * count instead.
 */
export function resolveEntityToolTableChrome(args: {
  isEntityHitsLayout: boolean;
  isProductTable: boolean;
  isOwnershipTab: boolean;
  clientOnlyFilterCount: number;
  hasClientSearchQuery: boolean;
  usesClientPagination: boolean;
  entityHitTotal: number;
  ownershipHitTotal: number;
  entityFilteredCount: number;
  ownershipFilteredCount: number;
  clientPagedRowCount: number;
}): EntityToolTableChrome {
  const isNarrowedInBrowser =
    args.clientOnlyFilterCount > 0 || args.hasClientSearchQuery;
  // Only a client-paginated table holds every row, so only there can a
  // filtered count stand in for the result total.
  const holdsFullResultSet = args.usesClientPagination;
  const isPageScopedFiltering = isNarrowedInBrowser && !holdsFullResultSet;
  // BiteDB entity-hits chrome always reports the backend Hit Total.
  const useFilteredCounts =
    isNarrowedInBrowser && holdsFullResultSet && !args.isEntityHitsLayout;

  const allMatchesCount = useFilteredCounts
    ? args.entityFilteredCount
    : args.entityHitTotal;
  const ownershipCount = useFilteredCounts
    ? args.ownershipFilteredCount
    : args.ownershipHitTotal;

  const tabCounts: EntityToolTabCounts = args.isProductTable
    ? { 'All Matches': allMatchesCount }
    : { 'All Matches': allMatchesCount, Ownership: ownershipCount };

  // The pager walks what the table can actually reach: the loaded (already
  // filtered and sorted) set under client pagination, one backend page at a
  // time otherwise.
  const totalForPagination = holdsFullResultSet
    ? args.clientPagedRowCount
    : args.isOwnershipTab
    ? args.ownershipHitTotal
    : args.entityHitTotal;

  return { tabCounts: tabCounts, totalForPagination: totalForPagination, isPageScopedFiltering: isPageScopedFiltering };
}
